import Modal from "../../../../../../components/base/Modal";
import type { Unit } from "../../../../../../store/admin/unitsStore";
import UnitVehicleAvatar from "../../../../../../components/vehicles/UnitVehicleAvatar";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../../../../utils/getDate";
import type { ThemeClasses } from "../../../../../../types/components";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";

interface UnitDetailsModalProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  unit: Unit;
  getStatusText: (status: string) => string;
  disableCRUD?: boolean;
  onClose: () => void;
  onEdit: () => void;
}

export default function UnitDetailsModal({
  themeClasses,
  isDarkMode,
  unit,
  getStatusText,
  disableCRUD = false,
  onClose,
  onEdit,
}: UnitDetailsModalProps) {
  const { t } = useTranslation("adminSections");
  const { isMonitor, isReadOnly } = useIsMonitor();
  const fieldClassName = `w-full text-sm px-4 py-3 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-50"
  } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text}`;

  const formatPolicyDate = (dateValue: string | null | undefined): string => {
    if (!dateValue) return "-";
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return "-";
      return date.toLocaleDateString();
    } catch {
      return "-";
    }
  };

  return (
    <Modal
      themeClasses={themeClasses}
      onClose={onClose}
      title={t("units.detailsTitle")}
      buttonAction={
        !isMonitor &&
        !isReadOnly && (
          <CRUDTooltip>
            <button
              onClick={onEdit}
              className="bg-primary-500 hover:bg-primary-400 text-gray-900 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-80 disabled:cursor-not-allowed"
              disabled={disableCRUD}
            >
              {t("units.actions.edit")}
            </button>
          </CRUDTooltip>
        )
      }
      content={
        <div className="space-y-6">
          {/* Su vehículo (carátula) con lápiz para cambiarlo en el garaje. */}
          <UnitVehicleAvatar
            vehicleId={unit.id}
            name={unit.name || ""}
            pointerModel={unit.pointer_model}
            bodyType={unit.type}
            size="hero"
            className="rounded-xl"
            fallback={
              <span className={`inline-flex items-center gap-2 text-sm ${themeClasses.textSecondary}`}>
                <i className="ri-navigation-line" aria-hidden /> Marcador clásico
              </span>
            }
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border border-gray-200 rounded-md p-4">
            <div>
              <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>ID</p>
              <p className={`${themeClasses.text} font-mono break-all`}>
                {unit.id || "VEH-001"}
              </p>
            </div>
            <div>
              <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>
                {t("units.fields.createdAt")}
              </p>
              <p className={`text-sm ${themeClasses.text}`}>
                {unit.createdAt?._seconds
                  ? formatDate(unit.createdAt._seconds)
                  : "-"}
              </p>
            </div>
            <div>
              <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>
                {t("units.fields.updatedAt")}
              </p>
              <p className={`text-sm ${themeClasses.text}`}>
                {unit.updatedAt?._seconds
                  ? formatDate(unit.updatedAt._seconds)
                  : "-"}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("units.fields.name")}
                </label>
                <div className={fieldClassName}>{unit.name}</div>
              </div>

              <div className="flex flex-col gap-2">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("units.fields.plate")}
                </label>
                <div className={fieldClassName}>{unit.plate}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex flex-col gap-2">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("units.fields.brand")}
                </label>
                <div className={fieldClassName}>{unit.brand}</div>
              </div>

              <div className="flex flex-col gap-2">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("units.fields.model")}
                </label>
                <div className={fieldClassName}>{unit.model}</div>
              </div>

              <div className="flex flex-col gap-2">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("units.fields.type")}
                </label>
                <div className={fieldClassName}>{unit.type?.toUpperCase()}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("units.fields.year")}
                </label>
                <div className={fieldClassName}>{unit.year}</div>
              </div>

              <div className="flex flex-col gap-2">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("units.fields.fuelType")}
                </label>
                <div className={fieldClassName}>
                  {unit.fuelType === "gasoline"
                    ? "Gasolina"
                    : unit.fuelType === "diesel"
                      ? "Diesel"
                      : unit.fuelType}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* TODO: Descomentar para integración futura de fuelCapacity */}
              {/* <div className="flex flex-col gap-2">
                <label
                  className={`text-sm font-semibold ${themeClasses.text}`}
                >
                  {t("units.fields.fuelCapacity")}
                </label>
                <div
                  className={`${themeClasses.bg} ${themeClasses.text} p-2 text-gray-700 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300 w-full`}
                >
                  {unit.fuelCapacity}
                </div>
              </div> */}

              <div className="flex flex-col gap-2">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("units.fields.status")}
                </label>
                <div className={fieldClassName}>
                  {getStatusText(unit.status?.name || "")}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("units.fields.policyId")}
                </label>
                <div className={fieldClassName}>
                  {unit.policy_id || "-"}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("units.fields.policyExpiryDate")}
                </label>
                <div className={fieldClassName}>
                  {formatPolicyDate(unit.policy_expiry_date)}
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}
